import React, { Fragment, useContext, useEffect, useRef, useState } from "react";
import * as yup from "yup";
import { useFormik } from "formik";
import SharedDataContext from "../../../SharedDataContext";
import { useMutation } from "react-query";
import { toast } from "react-hot-toast";
import { getCreditConsumptionLog, getUsaseAnalytics } from "../../../apis/workspace/security";
import Input from "../../Tags/Input";
import { Button } from "../../ui/button";
import Text from "../../Tags/Text";
import { useCaptureEvent } from "../../../hooks/useCaptureEvent";
import { ArrowDownTrayIcon } from "@heroicons/react/24/outline";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  Calendar,
  User,
  User2,
  UserCircle,
  UserCircle2,
  UserCircle2Icon,
} from "lucide-react";
import { ResponsivePie, ResponsivePieCanvas } from "@nivo/pie";
import CreditUsageGraph from "./CreditUsageGraph";
import { paginationConfig } from "../../../axios/constant";
import useUpdateQuery from "../../../hooks/useUpdateQuery";
import { getVariant, isEmptyObject, returnArray } from "../../../utils/GenericFn";
import Filter from "../../Filter";
import EditColumn from "../../EditColumn";
import Image from "../../Tags/Image";
import PrintDate from "../../Tags/PrintDate";
import { Badge } from "../../ui/badge";
import TableLoader from "../../Loaders/TableLoader";
import NoData from "../../NoData";
import Pagination from "../../Pagination";
import CreditPieGraph from "./CreditPieGraph";
import { useSearchParams } from "react-router-dom";

import relativeTime from "dayjs/plugin/relativeTime";
import dayjs from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat";
import { Menu, Transition } from "@headlessui/react";
import { Label } from "../../ui/label";
import { Skeleton } from "../../ui/skeleton";
dayjs.extend(localizedFormat);
dayjs.extend(relativeTime);

// analytics page data
const dateStaticFilters = [
  {
    name: "7 days",
    value: "7d",
  },
  {
    name: "30 days",
    value: "30d",
  },
  {
    name: "90 days",
    value: "90d",
  },
  {
    name: "1 year",
    value: "1y",
  },
];

export default function CreditConsumptionLog() {
  const captureEvent = useCaptureEvent();
  const userData = useContext(SharedDataContext);
  const { activeWorkspace } = userData || {};

  const [activeDateFilter, setActiveDateFilter] = useState({});
  const [searchParams, setSearchParams] = useSearchParams();

  const [analytics, setAnalytics] = useState({});
  const [filterQuery, setFilterQuery] = useState("");
  const [filters, setFilters] = useState({});
  const [pagination, setPagination] = useState(paginationConfig);
  const [sort, setSort] = useState("");

  const getUsaseAnalyticsFn = useMutation("get_usage_analytics", getUsaseAnalytics, {
    onSuccess(data, variables, context) {
      console.log(data);
      setAnalytics(data);
    },
  });
  const handleUsaseAnalytics = () => {
    if (filterQuery.includes("time_range=null")) {
      return;
    }
    getUsaseAnalyticsFn.mutate({
      query: filterQuery,
      options: {
        headers: { "X-Workspace-Id": activeWorkspace?.id },
      },
    });
  };

  useEffect(() => {
    setFilterQuery(generateQueryString());
  }, [pagination, filters, sort, activeDateFilter]);

  useEffect(() => {
    if (activeWorkspace?.id && filterQuery !== "") {
      handleUsaseAnalytics();
    }
  }, [activeWorkspace, filterQuery]);

  const generateQueryString = (forApi = true) => {
    const time_range = searchParams.get("time_range");
    const start_date = searchParams.get("start_date");
    const end_date = searchParams.get("end_date");
    let params = [];
    if (forApi) {
      params = [`page=${pagination.page}`, `per_page=${pagination.perPage}`, `sort=${sort}`];
    }
    for (let key in filters) {
      let item = filters[key];
      if (item.selectedScope && item.value) {
        let parsedValue = item.value
          .split(",")
          .map((v) => v.trim())
          .join("|");
        params.push(`filter[${item.selectedScope}]=${parsedValue}`);
      }
    }

    if (time_range === "custom" && start_date && end_date) {
      params.push(`time_range=${time_range}&start_date=${start_date}&end_date=${end_date}`);
    } else {
      params.push(`time_range=${time_range}`);
    }
    console.log("forApi", { forApi, sort, query: params.join("&") });
    return params.join("&");
  };

  const getFiltersObject = (data) => {
    if (isEmptyObject(data)) {
      // setSort("");
    } else {
      setPagination(paginationConfig);
    }
    setFilters(data);
  };
  const getPageNumber = (page) => {
    //console.log(page);
    setPagination((prev) => ({ ...prev, page }));
  };

  const handleSetSort = (column) => {
    if (sort === `-${column}`) {
      setSort(column);
    } else if (sort === column) {
      setSort(`-${column}`);
    } else {
      setSort(column);
    }
  };
  const [columns, setColumns] = useState({});
  const [sortedVisibleColumns, setSortedVisibleColumns] = useState(null);

  const getColumns = (columns) => {
    //console.log(columns);
    setColumns(columns);
    const visibleColumns = Object.values(columns).filter((column) => column.visible);
    const sortedVisibleColumns = visibleColumns.sort((a, b) => a.order - b.order);
    setSortedVisibleColumns(sortedVisibleColumns);
  };

  const setQueryString = useUpdateQuery();
  useEffect(() => {
    if (!isEmptyObject(filters)) {
      setQueryString(generateQueryString(false));
    }
  }, [filters]);

  const validationSchema = yup.object().shape({
    from_date: yup.string().required("From date is required"),
    to_date: yup.string().required("To date is required"),
  });

  const formik = useFormik({
    initialValues: {
      from_date: "",
      to_date: "",
    },
    validationSchema,
    onSubmit: (values) => {
      console.log("Submitted values:", values);
      handleUpdateTimeRange(values, true);
      formik.setSubmitting(false);
      setActiveDateFilter({
        name: "Custom Range",
        value: "custom",
      });
    },
  });

  function sanitizeFilename(name) {
    // Remove invalid characters
    const invalidChars = /[\\/:*?"<>|\x00-\x1F\x80-\x9F]/g; // Regex for invalid characters
    let sanitized = name.replace(invalidChars, "");

    // Prepend 'Enriched-' to the filename
    // sanitized = "Enriched-" + sanitized;

    // Truncate the filename if it's too long (200 characters is a common maximum)
    const maxLength = 200;
    sanitized = sanitized.substring(0, maxLength);

    // Avoid filenames that are reserved in Windows
    const reservedNames = [
      "CON",
      "PRN",
      "AUX",
      "NUL",
      "COM1",
      "COM2",
      "COM3",
      "COM4",
      "COM5",
      "COM6",
      "COM7",
      "COM8",
      "COM9",
      "LPT1",
      "LPT2",
      "LPT3",
      "LPT4",
      "LPT5",
      "LPT6",
      "LPT7",
      "LPT8",
      "LPT9",
    ];
    if (reservedNames.includes(sanitized.toUpperCase())) {
      sanitized = "_" + sanitized;
    }

    // Ensure the filename is not empty
    if (sanitized.trim() === "") {
      sanitized = "unnamed_file";
    }

    return sanitized;
  }

  const getCreditConsumptionLogFn = useMutation("credit_consumption_log", getCreditConsumptionLog, {
    onSuccess(data, variables, context) {
      console.log(data);
      toast.success("Logs Downloaded");
      formik.setSubmitting(false);
      formik.resetForm();

      // Sanitize the item name to be used as the file name
      const sanitizedFileName = sanitizeFilename("credit-consumption-log") + ".csv";

      // Create a URL for the blob
      const fileURL = window.URL.createObjectURL(new Blob([data]));
      // Create a temporary anchor tag
      const fileLink = document.createElement("a");
      fileLink.href = fileURL;
      fileLink.setAttribute("download", sanitizedFileName); // Set the sanitized file name for download
      document.body.appendChild(fileLink);

      // Programmatically click the link to start download
      fileLink.click();

      // Clean up
      fileLink.remove();
      window.URL.revokeObjectURL(fileURL);
    },
    onError(error) {
      formik.setSubmitting(false);
    },
  });

  const handleUpdateTimeRange = (obj, custom = false) => {
    setPagination(paginationConfig);
    const current = Object.fromEntries(searchParams.entries());
    const keys = {
      time_range: custom ? "custom" : obj?.value,
    };
    if (custom) {
      keys.start_date = obj?.from_date;
      keys.end_date = obj?.to_date;
    }
    setSearchParams({
      ...current,
      ...keys,
    });
  };

  useEffect(() => {
    const time_range = searchParams.get("time_range");
    if (time_range && time_range !== "custom") {
      const obj = dateStaticFilters.find((el) => el.value === time_range);
      if (obj) {
        setActiveDateFilter(obj);
        handleUpdateTimeRange(obj);
      } else {
        setActiveDateFilter(dateStaticFilters[0]);
        handleUpdateTimeRange(dateStaticFilters[0]);
      }
    } else if (time_range && time_range === "custom") {
      const start_date = searchParams.get("start_date");
      const end_date = searchParams.get("end_date");
      setActiveDateFilter({
        name: "Custom Range",
        value: "custom",
      });
      handleUpdateTimeRange({ from_date: start_date, to_date: end_date }, true);
      formik.setValues({ from_date: start_date, to_date: end_date });
    } else {
      setActiveDateFilter(dateStaticFilters[0]);
      handleUpdateTimeRange(dateStaticFilters[0]);
    }
  }, []);

  // download report
  function getDateRangeFromFilter(activeFilter) {
    let to_date = dayjs(); // current date
    let from_date;

    const start_date = searchParams.get("start_date");
    const end_date = searchParams.get("end_date");

    if (activeFilter?.value === "custom") {
      to_date = end_date;
      from_date = start_date;

      return {
        from_date: from_date,
        to_date: to_date,
      };
    } else {
      const unit = activeFilter.value.slice(-1); // 'd' or 'y'
      const amount = parseInt(activeFilter.value); // e.g., 7, 30, 1

      if (unit === "d") {
        from_date = to_date.subtract(amount, "day");
      } else if (unit === "y") {
        from_date = to_date.subtract(amount, "year");
      } else {
        throw new Error("Unsupported time unit");
      }
      return {
        from_date: from_date.format("YYYY-MM-DD"),
        to_date: to_date.format("YYYY-MM-DD"),
      };
    }
  }

  const handleDownloadReport = (obj) => {
    let params = [];

    for (let key in filters) {
      let item = filters[key];
      if (item.selectedScope && item.value) {
        let parsedValue = item.value
          .split(",")
          .map((v) => v.trim())
          .join("|");
        params.push(`filter[${item.selectedScope}]=${parsedValue}`);
      }
    }

    const paramsQuery = params.join("&");

    getCreditConsumptionLogFn.mutate({
      data: {
        from_date: obj.from_date,
        to_date: obj.to_date,
      },
      filterQuery: "",
      options: {
        headers: { "X-Workspace-Id": activeWorkspace?.id },
      },
    });
  };

  const buttonRef = useRef();
  return (
    <div>
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <Text size="lg">Usage analytics</Text>
          <Text size="p" className="mt-1">
            View and monitor credit usage across the workspace
          </Text>
        </div>
      </div>
      <div className="border-b border-border my-5"></div>
      <div className="flex justify-between">
        <div className="flex space-x-2 items-center">
          <div className="inline-flex rounded-md border bg-background overflow-hidden">
            {dateStaticFilters.map((item, index) => (
              <span
                onClick={() => {
                  searchParams.delete("start_date");
                  searchParams.delete("end_date");
                  setActiveDateFilter(item);
                  handleUpdateTimeRange(item);
                  formik.resetForm();
                }}
                className={`py-3 font-bold cursor-pointer px-4 ${index + 1 < dateStaticFilters.length ? "border-r" : ""} ${activeDateFilter?.value === item?.value ? "bg-[#E9EAEB] dark:bg-gray-100" : ""}`}
              >
                {item?.name}
              </span>
            ))}
          </div>

          <Menu as="div" className="relative inline-block text-left">
            <div>
              <Menu.Button ref={buttonRef}>
                <Button
                  variant="outline"
                  className={`h-[45px] ${searchParams.get("time_range") && searchParams.get("time_range") === "custom" && "bg-[#E9EAEB]  dark:bg-gray-100 "}`}
                >
                  <Calendar className="-ml-0.5 mr-1.5 h-5 w-5" aria-hidden="true" />
                  Custom Range{" "}
                  {searchParams.get("time_range") && searchParams.get("time_range") === "custom" && (
                    <>
                      : {searchParams.get("start_date")} - {searchParams.get("end_date")}
                    </>
                  )}
                </Button>
              </Menu.Button>
            </div>

            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items
                className="absolute left-0 z-10 mt-1 w-full min-w-[250px] origin-top-right 
                          divide-y divide-border rounded-md bg-background shadow-lg 
                          ring-1 ring-border ring-opacity-5 focus:outline-none"
              >
                <div className="p-4">
                  <form onSubmit={formik.handleSubmit} className="space-y-4">
                    <div>
                      <label htmlFor="from_date" className="block text-sm font-medium text-paragraph  mb-1">
                        From:
                      </label>
                      <Input
                        id="from_date"
                        name="from_date"
                        type="date"
                        min="2022-01-01"
                        max={new Date().toISOString().split("T")[0]}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.from_date}
                      />
                      {formik.touched.from_date && formik.errors.from_date && (
                        <p className="mt-1 text-red-500 text-sm">{formik.errors.from_date}</p>
                      )}
                    </div>
                    <div>
                      <label htmlFor="to_date" className="block text-sm font-medium text-paragraph mb-1">
                        To:
                      </label>
                      <Input
                        id="to_date"
                        name="to_date"
                        type="date"
                        min="2022-01-01"
                        max={new Date().toISOString().split("T")[0]}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.to_date}
                      />
                      {formik.touched.to_date && formik.errors.to_date && (
                        <p className="mt-1 text-red-500 text-sm">{formik.errors.to_date}</p>
                      )}
                    </div>
                    <div className="mt-6">
                      <Button
                        type="submit"
                        disabled={
                          formik.isSubmitting ||
                          !formik.isValid ||
                          (!formik.values.from_date && !formik.values.to_date)
                        }
                        onClick={() => {
                          buttonRef.current.click();
                        }}
                      >
                        Done
                      </Button>
                    </div>
                  </form>
                </div>
              </Menu.Items>
            </Transition>
          </Menu>
        </div>
        <div className="inline-flex space-x-3">
          <Button
            variant="purple_outline"
            onClick={() => {
              handleDownloadReport(getDateRangeFromFilter(activeDateFilter));
            }}
            disabled={getCreditConsumptionLogFn.isLoading}
          >
            <ArrowDownTrayIcon className="-ml-0.5 mr-1.5 h-5 w-5" aria-hidden="true" />
            Download report
            {getCreditConsumptionLogFn.isLoading && <div className="loader_circle ml-2"></div>}
          </Button>
          {/* <Button variant="outline">
            <Calendar className="-ml-0.5 mr-1.5 h-5 w-5" aria-hidden="true" />
            Select dates
          </Button> */}
          {/* <Button variant="outline">
            <User className="-ml-0.5 mr-1.5 h-5 w-5" aria-hidden="true" />
            Filter by users
          </Button> */}
        </div>
      </div>
      <div className="flex space-x-6 mt-6">
        {getUsaseAnalyticsFn.isLoading && (
          <>
            <div className="rounded-xl px-4 py-5 bg-white border border-border w-6/12 dark:bg-gray-100">
              <CreditPieGraph
                heading={
                  <div className="flex items-center space-x-3">
                    <Image src="/images/icons/untitledui/credit-usage-contact.svg" />
                    <Text size="md">Contact credit usage</Text>
                  </div>
                }
              />
            </div>
            <div className="rounded-xl px-4 py-5 bg-white border border-border w-6/12 dark:bg-gray-100">
              <CreditPieGraph
                heading={
                  <div className="flex items-center space-x-3">
                    <Image src="/images/icons/untitledui/credit-usage-company.svg" />
                    <Text size="md">Company credits used</Text>
                  </div>
                }
              />
            </div>
          </>
        )}
        {analytics?.data?.balances?.contact && !getUsaseAnalyticsFn.isLoading && (
          <div className="rounded-xl px-4 py-5 bg-white border border-border w-6/12 dark:bg-gray-100">
            <CreditPieGraph
              data={analytics?.data?.balances?.contact}
              heading={
                <div className="flex items-center space-x-3">
                  <Image src="/images/icons/untitledui/credit-usage-contact.svg" />
                  <Text size="md">Contact credit usage</Text>
                </div>
              }
              colorA="#7F56D9"
              colorB="#E9EAEB"
            />
          </div>
        )}
        {analytics?.data?.balances?.company && !getUsaseAnalyticsFn.isLoading && (
          <div className="rounded-xl px-4 py-5 bg-white border border-border w-6/12 dark:bg-gray-100">
            <CreditPieGraph
              data={analytics?.data?.balances?.company}
              heading={
                <div className="flex items-center space-x-3">
                  <Image src="/images/icons/untitledui/credit-usage-company.svg" />
                  <Text size="md">Company credits used</Text>
                </div>
              }
            />
          </div>
        )}
      </div>
      {getUsaseAnalyticsFn.isLoading && (
        <div className="rounded-xl px-4 py-5 bg-white border border-border dark:bg-gray-100 mt-6">
          <div className="flex justify-between mb-2 mt-2">
            <Text size="lg">Credit usage</Text>
          </div>
          <Skeleton className="h-[250px] w-full  mt-6" />
        </div>
      )}
      {analytics &&
        analytics?.data?.transactions &&
        analytics?.data?.transactions.length > 0 &&
        !getUsaseAnalyticsFn.isLoading && (
          <div className="rounded-xl px-4 py-5 bg-white border border-border dark:bg-gray-100 mt-6">
            <CreditUsageGraph
              sourceData={analytics?.graphData}
              dateRange={getDateRangeFromFilter(activeDateFilter)}
            />
          </div>
        )}

      <div>
        {analytics &&
          analytics?.data?.transactions &&
          (analytics?.data?.transactions.length > 0 || !isEmptyObject(filters)) && (
            <div className="mt-7 flex justify-between items-center">
              <div className="space-x-3 inline-flex items-center">
                {analytics && analytics.filterMetadata && (
                  <>
                    <span>Filters:</span>
                    <Filter
                      filterMetadata={analytics.filterMetadata}
                      getFiltersObject={getFiltersObject}
                      clearFilters={() => {
                        window.location.reload();
                      }}
                    />
                  </>
                )}
              </div>
              <div className="space-x-3">
                {analytics.columnMetadata && (
                  <EditColumn columnMetadata={analytics.columnMetadata} getColumns={getColumns} />
                )}
              </div>
            </div>
          )}
        <div>
          {analytics &&
            analytics?.data?.transactions &&
            analytics?.data?.transactions.length > 0 &&
            getUsaseAnalyticsFn.isSuccess && (
              <div className="mt-8 flow-root">
                <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                  <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                    <div className="overflow-hidden border border-border rounded-[--radius]">
                      <table className="min-w-full divide-y divide-border">
                        <thead className="bg-background">
                          <tr>
                            {returnArray(sortedVisibleColumns).map((column, index) => (
                              <th
                                key={index}
                                scope="col"
                                className={`px-3 py-3.5 text-left  font-normal   ${
                                  column.sortable ? "cursor-pointer" : ""
                                }`}
                                onClick={() => (column.sortable ? handleSetSort(column.key) : void 0)}
                              >
                                <span className={"inline-flex items-center"}>
                                  {column.display_name}
                                  {column.sortable && (
                                    <>
                                      {sort === "" || (sort !== column.key && sort !== `-${column.key}`) ? (
                                        <Image
                                          src="/images/icons/dual-arrow.svg"
                                          width="7"
                                          height="7"
                                          className="inline-block ml-1"
                                        />
                                      ) : (
                                        <>
                                          {sort === column.key && (
                                            <ArrowDownIcon width={12} className="inline-block ml-1" />
                                          )}
                                          {sort === `-${column.key}` && (
                                            <ArrowUpIcon width={12} className="inline-block ml-1" />
                                          )}
                                        </>
                                      )}
                                    </>
                                  )}
                                </span>
                              </th>
                            ))}
                            {/* <th
                              scope="col"
                              className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                            ></th> */}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border bg-background">
                          {analytics &&
                            returnArray(analytics?.data?.transactions).map((item, index) => (
                              <>
                                <tr
                                  key={item.id}
                                  className={`text-paragraph ${index % 2 !== 0 ? "bg-smoke" : ""}`}
                                >
                                  {returnArray(sortedVisibleColumns).map((column, colIndex) => {
                                    const colKey =
                                      columns &&
                                      Object.keys(columns).find((key) => columns[key].order === column.order);

                                    if (colKey === "user") {
                                      return (
                                        <td key={colIndex} className="whitespace-nowrap px-3 py-4">
                                          <div className="flex items-center">
                                            <div className="h-9 w-9 flex-shrink-0">
                                              <span className="bg-gray-100 inline-block text-gray-400 p-2 rounded-full">
                                                <User2 />
                                              </span>
                                            </div>
                                            <div className="ml-4">
                                              <div className="font-medium ">{item.user?.name}</div>
                                              <div className="mt-0 text-paragraph">{item.user?.email}</div>
                                            </div>
                                          </div>
                                        </td>
                                      );
                                    }
                                    if (colKey === "created_at") {
                                      return (
                                        <td key={colIndex} className="whitespace-nowrap px-3 py-4">
                                          <PrintDate date={item.created_at} />
                                        </td>
                                      );
                                    }
                                    if (colKey === "event") {
                                      return (
                                        <td key={colIndex} className="whitespace-nowrap px-3 py-4">
                                          {item[colKey] ? (
                                            <>
                                              <Badge
                                                variant={getVariant(item[colKey])}
                                                className={"capitalize"}
                                              >
                                                {item[colKey]}
                                              </Badge>
                                            </>
                                          ) : (
                                            "-"
                                          )}
                                        </td>
                                      );
                                    }

                                    return (
                                      <td key={colIndex} className="whitespace-nowrap px-3 py-4">
                                        {item[colKey] ? item[colKey] : "-"}
                                      </td>
                                    );
                                  })}
                                </tr>
                              </>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            )}
          {getUsaseAnalyticsFn.isLoading && <TableLoader />}

          {getUsaseAnalyticsFn.isSuccess &&
            analytics &&
            analytics?.data?.transactions &&
            analytics?.data?.transactions.length === 0 &&
            isEmptyObject(filters) && <NoData heading={"No analytics found!"} />}
          {getUsaseAnalyticsFn.isSuccess &&
            analytics &&
            analytics?.data?.transactions &&
            analytics?.data?.transactions.length === 0 &&
            !isEmptyObject(filters) && (
              <div className="p-10 border rounded-lg mt-8">
                <NoData heading={"No analytics found!"} filterMode={true} />
              </div>
            )}
          <div className="flex items-center justify-between  rounded-[--radius] py-3">
            <div className="flex-1">
              {analytics && analytics.pagination && analytics.pagination.total > 0 && (
                <Pagination data={analytics.pagination} getPageNumber={getPageNumber} />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
